import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DollarSign, CheckCircle2, Clock, Landmark } from "lucide-react";

const fmt = (n) => `$${(n || 0).toFixed(2)}`;

/** Driver app → "My Pay": the driver's own settlements (payouts).
 *  Price-blind: shows only THEIR payout amounts — never the trip fare. */
export const DriverPay = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/settlements/me").then(r => setData(r.data)).catch(() => setData({ settlements: [] }));
  }, []);

  if (!data) return <div className="px-5 text-[#A1A1AA] text-sm">Loading your pay…</div>;
  const list = data.settlements || [];

  return (
    <section className="px-5 mt-4" data-testid="driver-pay-tab">
      <div className="grid grid-cols-3 gap-2 mb-6">
        <StatCard label="Paid YTD" value={fmt(data.ytd_paid)} testid="pay-ytd"/>
        <StatCard label="All-time" value={fmt(data.paid_total)} testid="pay-total"/>
        <StatCard label="Pending" value={fmt(data.pending_total)} accent testid="pay-pending"/>
      </div>

      <PaymentSetup/>

      {list.length === 0 ? (
        <div className="border border-white/10 bg-[#0A0A0A] p-10 text-center">
          <DollarSign size={28} className="mx-auto text-[#71717a] mb-3"/>
          <div className="font-serif text-xl mb-1">No settlements yet</div>
          <p className="text-sm text-[#A1A1AA]">Your trip payouts will appear here after dispatch settles them.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map(s => <SettlementRow key={s.settlement_id} s={s}/>)}
        </div>
      )}
    </section>
  );
};

/** Payment setup: Stripe direct-deposit self-service + Zelle/Venmo handles.
 *  Bank details live at Stripe only — the app just shows enabled yes/no. */
function PaymentSetup() {
  const [stripe, setStripe] = useState(null);   // {connected, payouts_enabled}
  const [prefs, setPrefs] = useState(null);     // {zelle_handle, venmo_handle, preferred_method}
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/stripe/me/status").then(r => setStripe(r.data)).catch(() => setStripe({ connected: false }));
    api.get("/drivers/me/payout-prefs").then(r => setPrefs(r.data)).catch(() => setPrefs({}));
  }, []);

  const startStripe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/me/onboarding-link");
      window.location.href = r.data.url; // Stripe-hosted secure bank form
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start direct-deposit setup");
      setBusy(false);
    }
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await api.patch("/drivers/me/payout-prefs", prefs);
      toast.success("Payment preferences saved — dispatch can see them at payout time");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const ready = stripe?.connected && stripe?.payouts_enabled;

  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-4 mb-6" data-testid="payment-setup-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Landmark size={16} className="text-[#D4AF37]"/>
          <div>
            <div className="text-sm font-semibold text-[#F5F2EB]">Payment setup</div>
            <div className={`text-[11px] mt-0.5 ${ready ? "text-emerald-400" : "text-[#A1A1AA]"}`} data-testid="stripe-status-text">
              {ready ? "Direct deposit active" : stripe?.connected ? "Bank setup started — finish to get paid by deposit" : "Bank not linked yet"}
            </div>
          </div>
        </div>
        <button data-testid="payment-setup-toggle" onClick={() => setOpen(o => !o)}
          className="text-[10px] uppercase tracking-[0.16em] text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1.5 hover:bg-[#D4AF37]/10">
          {open ? "Close" : "Manage"}
        </button>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-white/10 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] mb-2">Direct deposit (bank)</div>
            <button data-testid="stripe-setup-btn" disabled={busy || ready} onClick={startStripe}
              className="btn-gold !py-2.5 !px-4 text-[10px] disabled:opacity-60 w-full">
              {ready ? "✓ Bank account connected" : busy ? "Opening secure bank form…" : stripe?.connected ? "Finish bank setup" : "Set up direct deposit"}
            </button>
            <p className="text-[10px] text-[#71717a] mt-1.5">Your bank details are entered on Stripe's secure page — Delux Limo never sees or stores them.</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] mb-2">Zelle / Venmo</div>
            <div className="space-y-2">
              <input data-testid="zelle-handle-input" className="w-full bg-black border border-white/15 rounded px-3 py-2.5 text-sm text-[#F5F2EB] placeholder-[#52525B] focus:outline-none focus:border-[#D4AF37]"
                placeholder="Zelle — phone or email" value={prefs?.zelle_handle || ""}
                onChange={(e) => setPrefs(p => ({ ...p, zelle_handle: e.target.value }))}/>
              <input data-testid="venmo-handle-input" className="w-full bg-black border border-white/15 rounded px-3 py-2.5 text-sm text-[#F5F2EB] placeholder-[#52525B] focus:outline-none focus:border-[#D4AF37]"
                placeholder="Venmo — @username" value={prefs?.venmo_handle || ""}
                onChange={(e) => setPrefs(p => ({ ...p, venmo_handle: e.target.value }))}/>
              <select data-testid="preferred-method-select" className="w-full bg-black border border-white/15 rounded px-3 py-2.5 text-sm text-[#F5F2EB] focus:outline-none focus:border-[#D4AF37]"
                value={prefs?.preferred_method || ""}
                onChange={(e) => setPrefs(p => ({ ...p, preferred_method: e.target.value }))}>
                <option value="">Preferred payout method — no preference</option>
                <option value="direct_deposit">Direct deposit (bank)</option>
                <option value="zelle">Zelle</option>
                <option value="venmo">Venmo</option>
              </select>
            </div>
            <button data-testid="save-payout-prefs-btn" disabled={saving} onClick={savePrefs}
              className="mt-3 w-full border border-white/20 text-[#F5F2EB] py-2.5 text-[10px] uppercase tracking-[0.16em] hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-50">
              {saving ? "Saving…" : "Save preferences"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div className={`border p-3 text-center ${accent ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-white/10 bg-[#0A0A0A]"}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#71717a] mb-1">{label}</div>
      <div className={`text-lg font-semibold ${accent ? "text-[#D4AF37]" : "text-[#F5F2EB]"}`}>{value}</div>
    </div>
  );
}

function SettlementRow({ s }) {
  const paid = s.status === "paid";
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-4" data-testid={`settlement-${s.settlement_id}`}>
      <div className="flex justify-between items-start">
        <div>
          <div className="overline">{s.confirmation_code || s.receipt_number}</div>
          <div className="text-[11px] text-[#71717a] mt-1">
            {paid ? `Paid ${s.paid_date || ""}` : "Approved — payout on the next run"}
            {s.method && ` · ${s.method}`}
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold text-[#F5F2EB]">{fmt(s.amount)}</div>
          <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.16em] ${paid ? "text-emerald-400" : "text-[#D4AF37]"}`}>
            {paid ? <CheckCircle2 size={10}/> : <Clock size={10}/>} {s.status}
          </span>
        </div>
      </div>
      {(s.driver_gratuity > 0 || s.custom_charge > 0) && (
        <div className="text-[11px] text-[#A1A1AA] mt-2 pt-2 border-t border-white/5">
          Fare {fmt(s.driver_fare)}
          {s.driver_gratuity > 0 && <> · Gratuity {fmt(s.driver_gratuity)}</>}
          {s.custom_charge > 0 && <> · {s.custom_charge_label || "Extra"} {fmt(s.custom_charge)}</>}
        </div>
      )}
    </div>
  );
}
